import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  Typography,
  Box,
  Container,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
} from "@mui/material";
import { FiberManualRecord } from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NavigateNext } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import PageHeaderWithBreadcrumbs from "../components/common/PageHeaderWithBreadcrumbs.js";
import Space from "../components/common/Space.js";
import NumberedList from "../components/common/NumberedList.js";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LuggageIcon from "@mui/icons-material/Luggage";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";

const iconStyle = {
  fontSize: 56,
  color: "#183B56",
  border: "2px solid #BFD6F6",
  borderRadius: "50%",
  padding: 2,
  background: "#fff",
  marginBottom: 1,
};

const ApplicationSteps = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const prefix = process.env.PUBLIC_URL || '';
  const steps = [
    {
      icon: `${prefix}/svg/emailVerification.svg`,
      title: t("applyForAnEvisa2.steps.item1.title"),
      description: t("applyForAnEvisa2.steps.item1.textBold"),
    },
    {
      icon: `${prefix}/svg/TravelInformation.svg`,
      title: t("applyForAnEvisa2.steps.item2.title"),
      description: t("applyForAnEvisa2.steps.item2.textBold"),
    },
    {
      icon: `${prefix}/svg/passportDetails.svg`,
      title: t("applyForAnEvisa2.steps.item3.title"),
      description: t("applyForAnEvisa2.steps.item3.textBold"),
    },
    {
      icon: `${prefix}/svg/PersonalDetails.svg`,
      title: t("applyForAnEvisa2.steps.item4.title"),
      description: t("applyForAnEvisa2.steps.item4.textBold"),
    },
    {
      icon: `${prefix}/svg/ReviewPayment.svg`,
      title: t("applyForAnEvisa2.steps.item5.title"),
      description: t("applyForAnEvisa2.steps.item5.textBold"),
    },
  ];

  return (
    <Box sx={{ mt: 4, mb: 4, px: { xs: 0, md: 0 }, position: "relative", minHeight: { xs: '420px', sm: 'auto' } }}>
      {/* Ligne horizontale unique derrière tous les cercles (desktop) */}
      <Box
        sx={{
          position: 'absolute',
          top: 65,
          left: 0,
          right: 0,
          height: 0,
          borderTop: '2px solid #0C3058',
          zIndex: 1,
          display: { xs: 'none', sm: 'block' },
        }}
      />
      {/* Ligne verticale unique derrière tous les cercles (mobile) */}
      <Box
        sx={{
          position: 'absolute',
          top: 35,
          left: 35,
          width: 2,
          height: 'calc(100% - 70px)',
          background: '#0C3058',
          zIndex: 1,
          display: { xs: 'block', sm: 'none' },
        }}
      />
      {/* Affichage horizontal desktop, vertical mobile - liste sémantique (ol/li) */}
      <Box
        component="ol"
        sx={{
          display: { xs: 'block', sm: 'flex' },
          alignItems: { xs: 'initial', sm: 'flex-start' },
          justifyContent: { xs: 'initial', sm: 'space-between' },
          position: 'relative',
          zIndex: 2,
          width: '100%',
          px: 0,
          // Remise à zéro des styles par défaut de <ol> pour conserver l'apparence
          listStyle: 'none',
          m: 0,
          p: 0,
        }}
      >
        {steps.map((step, idx) => (
          <Box
            key={idx}
            component="li"
            sx={{
              display: { xs: 'flex', sm: 'flex' },
              flexDirection: { xs: 'row', sm: 'column' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              width: { xs: '100%', sm: '130px' },
              minWidth: { xs: '100%', sm: '130px' },
              maxWidth: { xs: '100%', sm: '130px' },
              flex: { xs: 'none', sm: '0 1 130px' },
              p: 0,
              m: 0,
              position: 'relative',
              listStyle: 'none',
              mb: { xs: idx !== steps.length - 1 ? 4 : 0, sm: 0 },
            }}
          >
            {/* Ligne verticale sur mobile entre les cercles */}
            {idx > 0 && (
              <Box
                sx={{
                  display: { xs: 'block', sm: 'none' },
                  position: 'absolute',
                  left: 34,
                  top: -70,
                  width: 2,
                  height: 70,
                  background: '#0C3058',
                  zIndex: 1,
                }}
              />
            )}
            {/* Cercle */}
            <Box
              sx={{
                width: { xs: '70px', sm: '100px', md: '140px' },
                height: { xs: '70px', sm: '100px', md: '140px' },
                background: theme.palette.background.default,
                borderRadius: '50%',
                border: '2px solid #0C3058',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 2,
                flexShrink: 0,
                mt: { xs: idx === 0 ? 0 : 0, sm: 0 },
              }}
            >
              <Box
                component="img"
                src={step.icon}
                alt=""
                sx={{ width: '80%', height: '80%' }}
              />
            </Box>
            {/* Texte à droite du cercle sur mobile, en dessous sur desktop */}
            <Box
              sx={{
                textAlign: 'left',
                width: { xs: 'calc(100% - 80px)', sm: '140px' },
                mt: { xs: 0, sm: 1 },
                ml: { xs: 2, sm: 0 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: '#183B56', fontWeight: 300, fontSize: 14 }}
              >
                <span
                  style={{ color: '#BF360C', fontWeight: 700, marginRight: 4 }}
                >
                  {idx + 1}
                </span>{' '}
                {step.title}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: '#183B56', fontSize: 14, mb: 0 }}
              >
                {step.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ApplyForAnEvisa2 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const contentRef = useRef(null);
  const continueBtnRef = useRef(null);
  // Le formulaire peut revenir de 3 manières :
  //  1. window.location = "...?focus=apply-btn"  (cas fallback du formulaire)
  //  2. window.history.back()                    (cas par défaut, déclenché si le formulaire détecte
  //                                               que le referrer est cette page statique)
  //  3. Restauration bfcache                     (sous-cas de history.back avec cache)
  // On détecte les cas 1 et 2 au montage via l'URL et document.referrer.
  // Le cas 3 est géré séparément via l'événement pageshow.
  const detectFocusIntent = () => {
    if (typeof window === "undefined") return false;
    try {
      if (new URLSearchParams(window.location.search).get("focus") === "apply-btn") {
        return true;
      }
    } catch {}
    try {
      const formOrigin = process.env.REACT_APP_SITE;
      if (formOrigin && document.referrer && document.referrer.startsWith(formOrigin)) {
        return true;
      }
    } catch {}
    return false;
  };
  const shouldFocusOnMountRef = useRef(detectFocusIntent());
  const [showFocusOutline, setShowFocusOutline] = useState(false);
  const { i18n } = useTranslation();

  const focusContinueButtonNow = (node) => {
    if (!node) return;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    try {
      node.focus({ preventScroll: true, focusVisible: true });
    } catch {
      node.focus({ preventScroll: true });
    }
    setShowFocusOutline(true);
    requestAnimationFrame(() => {
      continueBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    // Nettoyer le paramètre ?focus de l'URL s'il existe
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("focus")) {
        url.searchParams.delete("focus");
        window.history.replaceState({}, "", url.toString());
      }
    } catch {}
  };

  // Callback ref : focus le bouton au moment exact où il est monté dans le DOM
  const handleContinueBtnRef = useCallback((node) => {
    continueBtnRef.current = node;
    if (!node || !shouldFocusOnMountRef.current) return;
    shouldFocusOnMountRef.current = false;
    focusContinueButtonNow(node);
  }, []);

  useEffect(() => {
    if (!shouldFocusOnMountRef.current && !detectFocusIntent()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    // Restauration bfcache (back/forward cache) lors du history.back()
    const handlePageShow = (event) => {
      if (!event.persisted) return;
      if (!detectFocusIntent()) return;
      const btn = continueBtnRef.current || document.getElementById("apply-btn");
      if (btn) focusContinueButtonNow(btn);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const handleContinueClick = () => {
    window.location.href = process.env.REACT_APP_SITE + `evisa?lang=${i18n.language}`;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        background: "#E8EDF6",
        py: { xs: 2, md: 0 },
      }}
    >
      <PageHeaderWithBreadcrumbs
        breadcrumbs={[
          { text: t("applyForAnEvisa2.breadcrumbs.home"), href: "/" },
          { text: t("applyForAnEvisa2.breadcrumbs.application") },
        ]}
        title={t("applyForAnEvisa2.header.title")}
        subtitle={t("applyForAnEvisa2.header.subtitle")}
        applyButtonText={false}
        sticky={false}
      />
      <Container maxWidth="lg">
        {/* Bloc principal */}
        <Box
          sx={{
            background: "#fff",
            borderRadius: 1,
            boxShadow: "0 2px 16px rgba(24,59,86,0.07)",
            px: { xs: 2, sm: 2, md: 6 },
            py: { xs: 2, sm: 3, md: 5 },
            mb: 5,
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            sx={{ color: "#183B56", mb: 2 }}
          >
            {t("applyForAnEvisa2.header.subtitle")}
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{ color: "#183B56", mb: 1, fontWeight: 400, fontSize: 18 }}
          >
            {t("applyForAnEvisa2.header.text1")}
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{ color: "#183B56", mb: 1, fontWeight: 400, fontSize: 18 }}
          >
            {t("applyForAnEvisa2.header.text2")}
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{ color: "#183B56", mb: 3, fontWeight: 400, fontSize: 18 }}
          >
            {t("applyForAnEvisa2.header.text3")}
          </Typography>
    
          <Typography
            variant="h6"
            component="h3"
            sx={{ color: "#183B56", mb: 2 }}
          >
            {t("applyForAnEvisa2.steps.title")}
          </Typography>
          <ApplicationSteps />
          <Space size="small" />
          <Typography
            variant="h6"
            component="h4"
            sx={{ color: "#183B56", mb: 2, fontWeight: 400, fontSize: 18 }}
          >
            <span style={{ color: "#BF360C", fontWeight: 700, marginRight: 4 }}>
              Note:
            </span>
            {t("applyForAnEvisa2.note")}
          </Typography>
          {/* <Typography
            variant="h6"
            component="h4"
            sx={{ color: "#183B56", mb: 2, fontWeight: 400, fontSize: 18 }}
          >
            {t("applyForAnEvisa2.note2")}
          </Typography> */}
          <Space size="small" />
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              onClick={() => navigate("/apply-for-an-evisa-b2-1")}
              variant="secondary"
              sx={{
                color: "#183B56",
                borderColor: "#183B56",
                borderRadius: 1,
                fontWeight: 400,
                fontSize: 18,
                mr: 2,
              }}
            >
                {t("applyForAnEvisa2.button")}
            </Button>
              <Button
                id="apply-btn"
                ref={handleContinueBtnRef}
                autoFocus={shouldFocusOnMountRef.current}
                variant="primary"
                onClick={handleContinueClick}
                onBlur={() => setShowFocusOutline(false)}
                sx={showFocusOutline ? {
                  outline: '3px solid #003056',
                  outlineOffset: '2px',
                } : {}}
              >
                {t("applyForAnEvisa2.button2")}
              </Button>
          </Box>
        </Box>
      </Container>
      <Space size="small" />
    </Box>
  );
};

export default ApplyForAnEvisa2;
